// Button: token-styled button atom. Variants map to the --dsw-alias-button-*
// fill families; no framework imports, all behavior via props.

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import clsx from 'clsx'
import css from './Button.module.css'

/** Visual variant, each backed by a --dsw-alias-button-* token family. */
export type ButtonVariant = 'primary' | 'ghost' | 'outline' | 'toolbar' | 'danger'

/** Control height: `md` is a 36px control, `sm` a 28px one. */
export type ButtonSize = 'md' | 'sm'

export type ButtonProps = {
  /** Visual family (default 'ghost'). */
  variant?: ButtonVariant
  /** 'md' 36px control with 12px corners or 'sm' 28px control with 8px corners. */
  size?: ButtonSize
  /** Optional leading 16px icon node. */
  icon?: ReactNode
  className?: string
  children?: ReactNode
} & ButtonHTMLAttributes<HTMLButtonElement>

/**
 * Render a button.
 * @param props - variant, size, optional leading icon, native button attributes.
 * @param ref - native button for focus management and overlay anchors.
 * @returns the button element.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'ghost', size = 'md', icon, className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={clsx(css.button, css[variant], css[size], className)}
      {...rest}
    >
      {icon != null && <span className={css.icon}>{icon}</span>}
      {children}
    </button>
  )
})
